<template>
  <div>
    <h5 class="mb-3">{{ title }}</h5>

    <div v-if="!company?.id" class="text-muted">{{ emptyText }}</div>

    <LoadingIndicatorComponent
      v-else-if="form.isLoading.value"
      inline
      message="Loading…"
    />

    <div
      v-else-if="form.loadFailed.value"
      class="text-danger small"
      :data-testid="`${testId}-load-error`"
    >
      {{ errorText }}
      <button
        type="button"
        class="btn btn-link btn-sm p-0 align-baseline"
        :data-testid="`${testId}-retry`"
        @click="form.load()"
      >
        Retry
      </button>
    </div>

    <form v-else @submit.prevent="form.save()">
      <slot />

      <button
        type="submit"
        class="btn btn-primary"
        :disabled="form.isSaving.value || saveDisabled"
      >
        <FontAwesomeIcon
          v-if="form.isSaving.value"
          icon="spinner"
          spin
          class="me-1"
        />
        Save
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
/**
 * Frame for an admin section that edits one company resource: heading, the "pick a company" and
 * loading states, the failed-load retry notice that deliberately renders no form, and the form
 * element with its save button.
 *
 * The form fields go in the default slot; `form` is the section's `useCompanySectionForm`, which
 * owns everything asynchronous. Saving a section whose load failed has to be impossible — it would
 * write blank defaults over the real configuration — hence the notice replacing the form rather
 * than only disabling the button.
 */
import type { CompanySectionForm } from "@/dashboard/admin/useCompanySectionForm";
import type { Company } from "@/types";

withDefaults(
  defineProps<{
    title: string;
    /** Prefix for the load-error and retry test ids, e.g. "fee-config". */
    testId: string;
    emptyText: string;
    errorText: string;
    company: Company | null;
    form: CompanySectionForm;
    /** Blocks saving while the fields are in a state the backend would reject anyway. */
    saveDisabled?: boolean;
  }>(),
  { saveDisabled: false }
);
</script>
